import { isEqual } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { twMerge } from 'tailwind-merge';

import MailIcon from '../../assets/svg/MailIcon';
import RefreshIcon from '../../assets/svg/RefreshIcon';
import { DashboardContent, TeamFilter } from '../../components/Dashboard';
import { SendReportModal } from '../../components/Modals';
import {
  Button,
  ButtonExport,
  DateRangeFilter,
  Loader,
  PERIOD,
  PeriodToggle,
  TenantAndTeamFilter,
} from '../../components/Shared';
import {
  EXTENDED_REPORT_FILE_FORMAT,
  getExtendedReportFormatOptions,
  REPORT_FILE_FORMAT,
  USER_ROLE,
} from '../../constants';
import {
  downloadFile,
  generatePDFFile,
  getNowInDefaultTimezoneAsDate,
} from '../../helpers';
import { useAuthContext } from '../../hooks';
import { Layout } from '../../layouts';
import {
  getDashboardContentCallerId,
  getDashboardContentTenantName,
  getFormattedFilters,
  getState,
  isPdfReportDisabled,
  STATE,
} from './helpers';

export const Dashboard = () => {
  const { userRole } = useAuthContext();

  const isAdmin = userRole === USER_ROLE.ADMIN;
  const isManager = userRole === USER_ROLE.MANAGER;

  const [isSendReportModalOpened, setIsSendReportModalOpened] = useState(false);

  const openSendReportModal = () => setIsSendReportModalOpened(true);
  const closeSendReportModal = () => setIsSendReportModalOpened(false);

  const initialFilters = {
    ...(isAdmin && { callerIds: [] }),
    ...(isManager && { callerId: null }),
    startDate: getNowInDefaultTimezoneAsDate(),
    endDate: getNowInDefaultTimezoneAsDate(),
    isLifetime: false,
  };

  const [filters, setFilters] = useState(initialFilters);

  const formattedFilters = useMemo(
    () => getFormattedFilters(filters, isAdmin, isManager),
    [filters, isAdmin, isManager],
  );

  const resetFilters = () => {
    setFilters(initialFilters);
  };

  const onChangePeriod = nextPeriod => {
    setFilters(prev => ({
      ...prev,
      isLifetime: nextPeriod === PERIOD.LIFETIME,
    }));
  };

  const [managerTeam, setManagerTeam] = useState([]);
  const [isManagerTeamLoading, setIsManagerTeamLoading] = useState(true);
  const [isManagerTeamError, setIsManagerTeamError] = useState(false);
  const [teams, setTeams] = useState([]);

  const fetchManagerTeam = useCallback(async () => {
    setIsManagerTeamLoading(true);
    setIsManagerTeamError(false);

    try {
      const response = await fetch('/get-manager-team');
      if (response.status !== 200) throw new Error();
      const data = await response.json();
      setIsManagerTeamLoading(false);
      setManagerTeam(data.team);
    } catch {
      setIsManagerTeamLoading(false);
      setIsManagerTeamError(true);
      toast.error('Failed to load manager team!', {
        position: 'top-right',
        autoClose: 3000,
      });
    }
  }, []);

  useEffect(() => {
    if (!isManager) return;
    fetchManagerTeam();
  }, [isManager, fetchManagerTeam]);

  const abortControllerRef = useRef(null);
  const [dashboardData, setDashboardData] = useState({});
  const [isDashboardDataLoading, setIsDashboardDataLoading] = useState(true);
  const [isDashboardDataError, setIsDashboardDataError] = useState(false);

  const fetchDashboardData = useCallback(async () => {
    setIsDashboardDataLoading(true);
    setIsDashboardDataError(false);

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();

    try {
      const response = await fetch(
        isAdmin ? '/admin/dashboard-data' : '/dashboard-data',
        {
          signal: abortControllerRef.current.signal,
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formattedFilters),
        },
      );
      if (response.status !== 200) throw new Error();
      const data = await response.json();
      const dashboardData = data.data;
      setIsDashboardDataLoading(false);
      setDashboardData(dashboardData);
    } catch (e) {
      if (e?.name === 'AbortError') return;
      setIsDashboardDataLoading(false);
      setIsDashboardDataError(true);
    }
  }, [isAdmin, formattedFilters]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const [isExportReportSubmitting, setIsExportReportSubmitting] =
    useState(false);

  const exportReport = async reportFormat => {
    setIsExportReportSubmitting(true);

    if (reportFormat === EXTENDED_REPORT_FILE_FORMAT.PDF) {
      await generatePDFFile(
        <DashboardContent
          callerId={
            isAdmin
              ? getDashboardContentCallerId(filters.callerIds, teams)
              : filters.callerId
          }
          tenantName={
            isAdmin
              ? getDashboardContentTenantName(filters.callerIds, teams)
              : dashboardData.tenantName
          }
          pdfMode
          dashboardData={dashboardData}
          filters={formattedFilters}
        />,
        pdf => pdf.save('Dashboard Report.pdf'),
      );
      setIsExportReportSubmitting(false);
      return;
    }

    try {
      const response = await fetch(
        isAdmin ? '/admin/export-dashboard-report' : '/export-dashboard-report',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ reportFormat, ...formattedFilters }),
        },
      );

      if (response.status !== 200) throw new Error();

      if (reportFormat === REPORT_FILE_FORMAT.XLSX) {
        const blob = await response.blob();
        downloadFile(blob, 'Dashboard Report.xlsx');
      }

      if (reportFormat === REPORT_FILE_FORMAT.CSV) {
        const blob = await response.blob();
        downloadFile(blob, 'Dashboard Report.csv');
      }

      setIsExportReportSubmitting(false);
    } catch {
      setIsExportReportSubmitting(false);
      toast.error('Something went wrong, please try again!', {
        position: 'top-right',
        autoClose: 3000,
      });
    }
  };

  const [isSendReportSubmitting, setIsSendReportSubmitting] = useState(false);

  const sendPdfReport = async data => {
    setIsSendReportSubmitting(true);
    const formData = new FormData();

    await generatePDFFile(
      <DashboardContent
        callerId={
          isAdmin
            ? getDashboardContentCallerId(filters.callerIds, teams)
            : filters.callerId
        }
        tenantName={
          isAdmin
            ? getDashboardContentTenantName(filters.callerIds, teams)
            : dashboardData.tenantName
        }
        pdfMode
        dashboardData={dashboardData}
        filters={formattedFilters}
      />,
      pdf => {
        const pdfBlob = pdf.output('blob');
        formData.append('pdfFile', pdfBlob, 'Dashboard Report');
      },
    );

    formData.append('sendTo', JSON.stringify(data.sendTo));
    formData.append('subject', data.subject);
    formData.append('message', data.message);

    try {
      const response = await fetch(
        isAdmin ? '/admin/send-pdf-report' : '/send-pdf-report',
        {
          method: 'POST',
          body: formData,
        },
      );

      if (response.status !== 200) throw new Error();

      setIsSendReportSubmitting(false);
      closeSendReportModal();
    } catch {
      setIsSendReportSubmitting(false);
      toast.error('Something went wrong, please try again!', {
        position: 'top-right',
        autoClose: 3000,
      });
    }
  };

  const sendReport = async data => {
    if (data.reportFormat === EXTENDED_REPORT_FILE_FORMAT.PDF) {
      sendPdfReport(data);
      return;
    }

    setIsSendReportSubmitting(true);

    try {
      const response = await fetch(
        isAdmin ? '/admin/send-dashboard-report' : '/send-dashboard-report',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...data, ...formattedFilters }),
        },
      );

      if (response.status !== 200) throw new Error();

      setIsSendReportSubmitting(false);
      closeSendReportModal();
    } catch {
      setIsSendReportSubmitting(false);
      toast.error('Something went wrong, please try again!', {
        position: 'top-right',
        autoClose: 3000,
      });
    }
  };

  const isFiltersChanged = !isEqual(
    getFormattedFilters(initialFilters, isAdmin, isManager),
    formattedFilters,
  );
  const areReportActionsDisabled =
    isDashboardDataLoading || isDashboardDataError;
  const isButtonExportDisabled =
    areReportActionsDisabled || isExportReportSubmitting;
  const isButtonSendReportDisabled = areReportActionsDisabled;
  const state = getState(isDashboardDataLoading, isDashboardDataError);

  return (
    <Layout>
      <div className="flex flex-col gap-4 h-full overflow-hidden">
        <div className="flex justify-between gap-3 overflow-auto shrink-0">
          <div className="flex gap-3 shrink-0">
            {isAdmin && (
              <TenantAndTeamFilter
                callerIds={filters.callerIds}
                onChangeCallerIds={setFilters}
                onChangeTeams={setTeams}
              />
            )}
            {isManager && !isManagerTeamError && (
              <TeamFilter
                callerId={filters.callerId}
                onChangeCallerId={setFilters}
                managerTeam={managerTeam}
                isManagerTeamLoading={isManagerTeamLoading}
              />
            )}
            <PeriodToggle
              value={filters.isLifetime ? PERIOD.LIFETIME : PERIOD.DAILY}
              onChange={onChangePeriod}
            />
            <div
              className={twMerge(
                filters.isLifetime && 'opacity-50 pointer-events-none',
              )}
            >
              <DateRangeFilter
                startDate={filters.startDate}
                endDate={filters.endDate}
                onChangeDateRange={setFilters}
              />
            </div>
            {isFiltersChanged && (
              <Button
                title="Clear All"
                variant="text"
                onClick={resetFilters}
                className="!bg-transparent hover:!bg-transparent focus:!bg-transparent text-brand-900 p-2"
              />
            )}
          </div>
          {(isAdmin || isManager) && (
            <div className="flex gap-3 shrink-0">
              <ButtonExport
                disabled={isButtonExportDisabled}
                onClick={exportReport}
                options={getExtendedReportFormatOptions(
                  isAdmin
                    ? isPdfReportDisabled(filters.callerIds, teams)
                    : false,
                )}
              />
              <Button
                title="Send Report"
                variant="blue"
                iconStart={
                  <MailIcon
                    className={twMerge(
                      'w-5 h-5 text-brand-900',
                      isButtonSendReportDisabled && 'text-base-400',
                    )}
                  />
                }
                disabled={isButtonSendReportDisabled}
                onClick={openSendReportModal}
              />
            </div>
          )}
        </div>
        {state === STATE.IS_DATA_LOADING && (
          <div className="h-full flex justify-center items-center">
            <Loader />
          </div>
        )}
        {state === STATE.IS_DATA_ERROR && (
          <div className="h-full flex justify-center items-center flex-col gap-3 bg-brand-300/[0.08] rounded-[20px]">
            <div className="flex justify-center items-center flex-col gap-1.5">
              <span className="text-base-1000 text-[22px]/[28px] font-semibold">
                Failed to load data
              </span>
              <span className="text-base-800 text-[16px]/[24px]">
                Error occurred while loading. Try again.
              </span>
            </div>
            <Button
              title="Reload"
              variant="text"
              className="text-destructive-500 !bg-transparent"
              iconStart={
                <RefreshIcon className="w-5 h-5 text-destructive-500" />
              }
              onClick={fetchDashboardData}
            />
          </div>
        )}
        {state === STATE.HAS_DATA && (
          <DashboardContent
            filters={formattedFilters}
            dashboardData={dashboardData}
          />
        )}
        {isSendReportModalOpened && (
          <SendReportModal
            options={getExtendedReportFormatOptions(
              isAdmin ? isPdfReportDisabled(filters.callerIds, teams) : false,
            )}
            open={isSendReportModalOpened}
            disabled={isSendReportSubmitting}
            onSubmit={sendReport}
            onClose={closeSendReportModal}
          />
        )}
      </div>
    </Layout>
  );
};
